class ICorePreloader {
    static MIN_DURATION = 3600;

    static HOLD_AT_COMPLETE = 1400;

    static STORAGE_KEY = 'igx-preloader-seen';

    static RING_CIRCUMFERENCE = 339.292;

    element = null;

    progressFill = null;

    progressValue = null;

    ringProgress = null;

    startTime = 0;

    loadComplete = false;

    rafId = null;

    eventHandler = {};

    constructor(element) {
        this.element = element;

        if (!this.element) {
            return;
        }

        if (document.documentElement.classList.contains('igx-preloader-skip')) {
            this.#removeElement();
            return;
        }

        this.progressFill = this.element.querySelector('.igx-preloader__progress-fill');
        this.progressValue = this.element.querySelector('.igx-preloader__progress-value');
        this.ringProgress = this.element.querySelector('.igx-preloader__ring-progress');

        this.#ensureRingGradient();
        this.startTime = performance.now();
        this.#initEvents();
        this.#tick();
    }

    static init() {
        const el = document.getElementById('igxPreloader');

        if (!el || document.documentElement.classList.contains('igx-preloader-skip')) {
            return null;
        }

        const instance = new ICorePreloader(el);
        window.$iCore = window.$iCore || {};
        window.$iCore.plugins = window.$iCore.plugins || {};
        window.$iCore.plugins.icore_preloader = instance;

        return instance;
    }

    #ensureRingGradient() {
        if (document.getElementById('igxPreloaderRingGrad')) {
            return;
        }

        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', '0');
        svg.setAttribute('height', '0');
        svg.style.position = 'absolute';

        svg.innerHTML = '<defs><linearGradient id="igxPreloaderRingGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#e3c987"/><stop offset="1" stop-color="#c9a24b"/></linearGradient></defs>';

        document.body.appendChild(svg);
    }

    #initEvents() {
        this.eventHandler.load = this.#onLoad.bind(this);

        if (document.readyState === 'complete') {
            this.loadComplete = true;
        } else {
            window.addEventListener('load', this.eventHandler.load);
        }
    }

    #onLoad() {
        this.loadComplete = true;
    }

    #tick() {
        const elapsed = performance.now() - this.startTime;
        const timeProgress = Math.min(elapsed / ICorePreloader.MIN_DURATION, 1);
        const loadProgress = this.loadComplete ? 1 : Math.min(elapsed / (ICorePreloader.MIN_DURATION * 1.4), 0.92);
        const progress = Math.min(Math.max(timeProgress * 0.55 + loadProgress * 0.45, 0), 0.98);

        this.#setProgress(progress);

        if (timeProgress >= 1 && this.loadComplete) {
            this.#finish();
            return;
        }

        this.rafId = requestAnimationFrame(() => this.#tick());
    }

    #setProgress(ratio) {
        const pct = Math.round(ratio * 100);

        if (this.progressFill) {
            this.progressFill.style.width = `${pct}%`;
        }

        if (this.progressValue) {
            this.progressValue.textContent = `${pct}%`;
        }

        if (this.ringProgress) {
            const offset = ICorePreloader.RING_CIRCUMFERENCE * (1 - ratio);
            this.ringProgress.style.strokeDashoffset = `${offset}`;
        }
    }

    #finish() {
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }

        this.#setProgress(1);
        this.element.classList.add('is-complete');

        window.setTimeout(() => {
            this.#playExit();
        }, ICorePreloader.HOLD_AT_COMPLETE);
    }

    #playExit() {
        if (!this.element) {
            return;
        }

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reducedMotion) {
            this.#complete();
            return;
        }

        this.element.classList.add('is-exiting');
        this.element.classList.remove('is-complete');

        const gsap = window.gsap || (window.$iCore && window.$iCore.helper && window.$iCore.helper.animator);
        const stage = this.element.querySelector('.igx-preloader__stage');
        const markWrap = this.element.querySelector('.igx-preloader__mark-wrap');
        const orbs = this.element.querySelectorAll('.igx-preloader__orb');

        if (gsap) {
            const tl = gsap.timeline({
                onComplete: () => this.#complete(),
            });

            tl.to(markWrap, {
                scale: 1.06,
                duration: 0.45,
                ease: 'power2.out',
            }, 0);

            tl.call(() => {
                document.documentElement.classList.remove('igx-preloader-active');
                document.documentElement.classList.add('igx-preloader-revealing');
            }, null, 0.35);

            tl.to([stage, orbs], {
                opacity: 0,
                y: -10,
                duration: 0.65,
                ease: 'power2.inOut',
            }, 0.2);

            tl.to(this.element, {
                opacity: 0,
                duration: 0.85,
                ease: 'power2.inOut',
            }, 0.35);

            return;
        }

        document.documentElement.classList.remove('igx-preloader-active');
        document.documentElement.classList.add('igx-preloader-revealing');
        this.element.style.animation = 'igxPreloaderFadeOut .85s ease-in-out forwards';
        window.setTimeout(() => this.#complete(), 900);
    }

    #complete() {
        try {
            sessionStorage.setItem(ICorePreloader.STORAGE_KEY, '1');
        } catch (e) {}

        document.documentElement.classList.remove('igx-preloader-active');
        document.documentElement.classList.remove('igx-preloader-revealing');
        document.documentElement.classList.add('igx-preloader-skip');
        this.element.classList.add('is-done');

        document.dispatchEvent(new CustomEvent('igx:preloader:done'));

        this.#removeElement();
    }

    #removeElement() {
        if (this.element && this.element.parentNode) {
            this.element.parentNode.removeChild(this.element);
        }

        this.element = null;
    }

    destroy() {
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }

        window.removeEventListener('load', this.eventHandler.load);
    }
}

export default ICorePreloader;
